import { useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { Collection, CollectionItem, Video } from "@/api";
import { checkVideoFile, downloadFile } from "@/utils";
import { TEMPLATE_WEBSITE } from "@/const.ts";
import { useScrollPosition } from "@/providers/context";

interface NavigateToItemProps {
  parentId?: number | string | null; // Required for videos to show up next
  courseId?: number | string | null; // Required for course view
  item: Pick<CollectionItem, "Type" | "ChildId">;
  autoPlay?: boolean;
  fullItem?: Collection | Video;
  state?: {
    fromQuery: string;
  };
}

export const useNavigateToItem = () => {
  const navigate = useNavigate();
  const { clearScrollPosition } = useScrollPosition();

  const navigateToItem = useCallback(
    ({
      parentId,
      courseId,
      item,
      autoPlay,
      fullItem,
      state,
    }: NavigateToItemProps) => {
      const isWebsite =
        fullItem?.Type === "collection" &&
        (fullItem as Collection)?.TemplateId === TEMPLATE_WEBSITE;
      const URL = isWebsite && (fullItem as Collection).Properties?.Website;

      if (URL) {
        window.open(URL, "_blank", "noopener,noreferrer");
        return;
      }

      const isFile =
        fullItem?.Type === "video" && !!checkVideoFile(fullItem as Video);
      const fileName = isFile && (fullItem as Video).OriginalFilename;

      if (fileName) {
        downloadFile(fileName);
      } else if (item.Type === "collection" || item.Type === "tab") {
        const pathname = `/collection/${item.ChildId}`;
        // Clear scroll position of collection we're about to navigate to
        // We only want to use saved position when navigating back to a collection
        clearScrollPosition(pathname);
        navigate(pathname, { state });
      } else if (item.Type === "video") {
        const path = `/video/${item.ChildId}`;
        const params = new URLSearchParams();
        if (parentId) {
          params.set("parent_id", parentId.toString());
        }
        if (courseId) {
          params.set("course_id", courseId.toString());
        }
        navigate(`${path}?${params.toString()}`, { state: { autoPlay } });
      } else {
        navigate(`${item.Type}/${item.ChildId}`);
      }
    },
    [navigate, clearScrollPosition],
  );

  return { navigateToItem };
};
